/**
 * previewRows — adapts one AI Review RUN into the three inputs `ContactsTable`
 * expects, so the preview and the Import Detail page render the same component.
 *
 * Why an adapter at all. The two surfaces read different endpoints. Import
 * Detail reads `GET /imports/{id}/contacts`, where the scores have already been
 * persisted as annex fields and arrive on the row (`row.annex`) beside
 * descriptors the server built (`columns`). The preview reads
 * `GET /ai-reviews/{id}`, whose result is a score matrix and nothing else — it
 * describes a run that has NOT been applied, so no annex row exists yet. These
 * functions build, client-side, the exact shapes the server would later persist.
 *
 * THE JOIN KEY IS THE CRITERION NAME, and that is load-bearing rather than
 * incidental. The backend's `build_annex_columns` sets an annex column's `key`
 * AND `label` to the criterion name, so `AiReviewCellScore.criterion_name`
 * addresses the same column on both surfaces. `ImportDetailPage` already relies
 * on it for its own `rationaleFor`. Change either side to a criterion id and
 * both surfaces break together — which is the good failure, but it is worth
 * knowing that this file is one of the two places holding that convention.
 *
 * No React and no I/O: the fetch lives in `AiReviewPage`, so everything here is
 * a pure transform and is unit-tested as one.
 */
import type { AiReviewResult, AnnexColumn, ImportContactRow } from "@nu-assist/contracts";

/**
 * The reserved total column the apply worker writes onto every derived import.
 *
 * NOT a name this file chose: it is `TOTAL_CRITERION_NAME` in
 * `app/web/v2/ai_reviews/schemas.py`, and the create endpoint REFUSES a
 * criterion called this precisely so the worker's own column cannot collide
 * with a recruiter's. Synthesising it here is what makes the preview show the
 * same total column, under the same header, in the same position as the
 * Import Detail page — rather than a second total of our own invention beside
 * the one the detail page already had.
 *
 * THIS IS THE THIRD COPY OF THE STRING, and none of the three can import the
 * others (two Python packages and one TS one):
 *
 *   - `api/app/web/v2/ai_reviews/schemas.py`   `TOTAL_CRITERION_NAME`
 *   - `workers/ai_review_apply_worker/domain.py` `TOTAL_COLUMN`
 *   - here                                      `TOTAL_COLUMN_KEY`
 *
 * The worker's copy is pinned equal by its own test; `previewRows.pin.test.ts`
 * does the same for this one by reading the api's source. Rename any one of
 * them without the other two and the preview grows a second total column
 * beside the file's, which is the exact bug this file exists to have removed.
 */
export const TOTAL_COLUMN_KEY = "AI Review Total";

/**
 * The run's criteria as annex column descriptors, PLUS the reserved total.
 *
 * Two things here mirror the server rather than being free choices, and both
 * are what "the same view" reduces to in practice:
 *
 *  - the criteria are `kind: "score"` (badges) while the total is
 *    `kind: "value"` (plain text) — exactly what `build_annex_columns` derives
 *    from the persisted rows, where only the criteria carry a numeric `score`;
 *  - the reserved total comes FIRST, then the criteria in the RUN'S OWN order
 *    (`run.criteria` — the order the recruiter typed them, preserved through
 *    `criteria_config`). This mirrors `find_annex_fields`, which orders
 *    `is_total DESC, ordinal ASC NULLS LAST, criterion_name ASC`: the total
 *    pins left for every row vintage, and `ordinal` (migration 0039) carries
 *    the criterion's input position, so the detail page reads the same order
 *    the run reports here.
 *
 *    Total-left supersedes cl.4 2026-08-20's "Total lands right" (2026-09-15).
 *    Note the total row still persists `ordinal = len(criteria)`; it is
 *    `is_total` leading the server's ORDER BY that pins it, not its ordinal.
 *
 *    A `.sort()` with a glibc-mirroring `Intl.Collator` stood here until cl4
 *    sub-task 06 — the repository ordered annex rows alphabetically by
 *    `criterion_name`, so this file re-derived THAT order (collation quirks
 *    and all) to keep the two views agreeing. Input order removes the whole
 *    problem class: array position is the contract on both sides, and no
 *    locale/collation behaviour is in play. Do not reintroduce a sort here —
 *    the detail page for a LEGACY import (pre-0039 rows, NULL ordinals) still
 *    renders its CRITERIA alphabetically via the repository's `criterion_name`
 *    fallback, but a legacy run is never previewed: this function only ever
 *    renders a run mounted in-session, whose rows are post-0039 by
 *    construction. The total's position is vintage-independent either way.
 */
export function criteriaToAnnexColumns(run: AiReviewResult): AnnexColumn[] {
  return [
    { key: TOTAL_COLUMN_KEY, label: TOTAL_COLUMN_KEY, kind: "value" },
    ...run.criteria.map((c): AnnexColumn => ({ key: c.name, label: c.name, kind: "score" })),
  ];
}

/**
 * Join a run's scores onto the real contact rows fetched for the same ids.
 *
 * Returns the rows in the RUN's order, not the fetch's: the run decided which
 * contacts the sample contains and in what order it reports them, and the
 * contacts endpoint sorts by `(first_name, last_name, id)`. Letting the fetch
 * order win would reshuffle the preview under a sort nobody asked for.
 *
 * A run item whose contact is missing from `contacts` is DROPPED rather than
 * rendered as a skeleton — it means the row was deleted between the run and
 * this fetch, and a row with scores but no name reads as a bug. A contact with
 * no matching run item is dropped for the mirror-image reason: the preview
 * shows what was scored.
 *
 * Existing `annex` values are preserved and the run's scores layered ON TOP, so
 * re-reviewing an already-AI-reviewed list previews the NEW scores while
 * leaving any unrelated annex field the row already carried.
 */
export function joinScores(contacts: ImportContactRow[], run: AiReviewResult): ImportContactRow[] {
  const byId = new Map(contacts.map((c) => [c.import_contact_id, c]));
  const rows: ImportContactRow[] = [];
  for (const item of run.items ?? []) {
    const contact = byId.get(item.import_contact_id);
    if (!contact) continue;
    const annex: Record<string, string | number | null> = { ...contact.annex };
    for (const s of item.scores) annex[s.criterion_name] = s.score;
    // READ the server's total; do NOT recompute it. Both halves matter.
    //
    //  - WHICH total: the apply worker persists the WEIGHTED total
    //    (`ai_review_apply_worker/domain.py` `contact_totals` -> `weighted_total`,
    //    Σ(score×weight_raw)/Σweight_raw), not the simple mean. Those coincide
    //    only while every `weight_raw` is 1, which holds today solely because
    //    `AiReviewPage` hardcodes it — the API accepts 0..100 per criterion and
    //    ships `average_total` beside this one as a stated FE toggle target. A
    //    recomputed mean would silently diverge the day a weight control lands.
    //  - WHICH ROUNDING: `weighted_total` arrives already `round(x, 2)` from the
    //    API, the same rounding the worker applies before formatting. So
    //    `toFixed(2)` here only pads — no rounding mode is in play. Recomputing
    //    would put one in play, and JS rounds halves away from zero where Python
    //    rounds to even: 8 criteria (the documented max) summing to 17 gives
    //    2.125, which is "2.13" recomputed and "2.12" in the file.
    //
    // Reading the field makes the two surfaces agree by construction rather
    // than by a coincidence this file would have to keep re-earning.
    // UNCONDITIONAL, matching the worker. `build_source_annex_rows` appends the
    // reserved total row OUTSIDE its per-criterion loop, with
    // `f"{totals.get(contact_id, 0.0):.2f}"` — so a contact the model returned
    // no cells for gets "0.00" in the derived file, not a blank. Guarding on
    // `scores.length` here rendered a dash for that contact and `0.00` on the
    // detail page: a preview/detail divergence in the one cell this whole
    // feature is about. The API's `_safe_weighted_total` returns 0.0 for the
    // same contact, so reading the field reproduces the worker exactly.
    annex[TOTAL_COLUMN_KEY] = item.weighted_total.toFixed(2);
    rows.push({ ...contact, annex });
  }
  return rows;
}

/**
 * Rows built from the RUN ALONE — name + scores, no profile half.
 *
 * For an APPLIED run the contacts fetch is disabled: `rekey_scores` has moved
 * the item ids onto the derived file's copies, so asking the parent for them
 * returns nothing. But the run still carries everything the old preview table
 * rendered — `first_name`, `last_name`, the score cells and `weighted_total` —
 * and `dev` displayed exactly that. Discarding it to show a sentence instead
 * would lose a table the recruiter used to have.
 *
 * How that state is REACHED, stated precisely because an earlier revision of
 * this note overstated it as "just by opening a people list that was already
 * reviewed": `AiReviewPage` calls `setReviewId(null)` on every selection change
 * and never restores a run id from `useLatestAiReview`, so opening a reviewed
 * list does NOT re-mount its run. In-session the reachable path is the
 * `output_import_id == null` arm, where the page stays mounted through
 * `applied` because there is no derived file to navigate to.
 *
 * So an applied run renders these: same columns, badges, rationale and total as
 * any other, with the profile columns simply empty because that half genuinely
 * lives on the child now. Same shape `dev` had, and no fetch required.
 */
export function scoreOnlyRows(run: AiReviewResult | undefined): ImportContactRow[] {
  return (run?.items ?? []).map((item) => {
    const annex: Record<string, string | number | null> = {};
    for (const s of item.scores) annex[s.criterion_name] = s.score;
    annex[TOTAL_COLUMN_KEY] = item.weighted_total.toFixed(2);
    return {
      import_contact_id: item.import_contact_id,
      first_name: item.first_name ?? null,
      last_name: item.last_name ?? null,
      annex,
    } as ImportContactRow;
  });
}

/**
 * The run's per-cell rationales, in the lookup shape `ContactsTable` takes.
 *
 * Mirrors `ImportDetailPage`'s own `rationaleFor` exactly — same nesting, same
 * key, same `undefined` for a miss — because the whole point of the card behind
 * this file is that the rationale renders identically on both surfaces.
 */
export function rationaleForRun(
  run: AiReviewResult | undefined
): ((contactId: string, criterionKey: string) => string | undefined) | undefined {
  if (!run?.items) return undefined;
  const byContact = new Map<string, Map<string, string>>();
  for (const item of run.items) {
    const byCriterion = new Map<string, string>();
    for (const s of item.scores) byCriterion.set(s.criterion_name, s.rationale);
    byContact.set(item.import_contact_id, byCriterion);
  }
  return (contactId, criterionKey) => byContact.get(contactId)?.get(criterionKey);
}

/** The endpoint's own ceiling, mirrored: `GET /imports/{id}/contacts` caps both
 *  `page_size` at 100 and `ids` at 25, and 422s past either. The 25 is set by
 *  the WAF, not by the page size: each id costs 41 bytes of query string and
 *  `SizeRestrictions_QUERYSTRING` blocks past 2,048, so a bigger request is
 *  403'd at the edge with a body that never reaches the logs.
 *
 *  REACHABLE — an earlier version of this note said "unreachable today (a full
 *  run leaves the page on the 202)", which `scoredContactIds` below now
 *  contradicts and is right to: the create callback returns early when
 *  `output_import_id` is null, so a full run over a real import leaves this
 *  page mounted with far more than 25 scored items. Pinned to the published
 *  bound by `previewRows.pin.test.ts`, because the coupling is invisible from
 *  here and the failure it would produce — a 422 surfacing as "the candidates
 *  were scored, but their details could not be loaded" — names the wrong
 *  cause. */
export const MAX_PREVIEW_IDS = 25;

/** The contact ids a run scored — the `ids` the preview fetches by.
 *
 *  Truncated to `MAX_PREVIEW_IDS` so the request the page builds is always one
 *  the endpoint will accept.
 *
 *  TRUNCATION IS NOT SILENT — see `scoredContactCount`. Quietly showing the
 *  first 25 is the same silent-wrong-answer shape this PR refused at the API
 *  (where an over-long `ids` is a 422 rather than a narrowed result), and it is
 *  reachable: a FULL run leaves this page mounted whenever
 *  `output_import_id` is null (the legacy / deploy-skew arm at
 *  `AiReviewPage`'s create callback), and a full run over a real import scores
 *  far more than 25. The caller compares the two counts and says so. */
export function scoredContactIds(run: AiReviewResult | undefined): string[] {
  return (run?.items ?? []).map((i) => i.import_contact_id).slice(0, MAX_PREVIEW_IDS);
}

/** How many contacts the run actually scored, BEFORE truncation — the
 *  denominator that makes the truncation visible. */
export function scoredContactCount(run: AiReviewResult | undefined): number {
  return (run?.items ?? []).length;
}
